import React, { forwardRef } from 'react';
import {
	ShoppingItem,
	SofaConfiguration,
	Settings,
	Platform,
	DeviceType,
	Orientation,
	SofaRangeName,
	WriteDirection,
	CoverItem,
	YmalSectionType,
	YmalType,
} from 'sofa-reborn-react';
import HeaderContainer from '../../containers/HeaderContainer';
import { StyledSummaryPage, SummaryPageSection } from './SummaryPageStyle';
import CheckoutHeader from '../../components/CheckoutHeader';
import SaveViewContainer from '../../containers/SaveViewContainer';
import YmalViewContainer from '../../containers/YmalViewContainer';

import { localeService } from '../../services';

import { IProductListSection } from '@inter-ikea-kompis/component-product-list';
import { AddToBagStateEnum } from '@inter-ikea-kompis/component-add-to-bag';
import { KompisSummaryPage } from '@inter-ikea-kompis/react-components';
import { ISummaryPageInlineMessage } from '@inter-ikea-kompis/component-summary-page';
import ISummaryPageVisibleModal from '@inter-ikea-kompis/component-summary-page/lib/types/ISummaryPageVisibleModal';
import SummaryPageConfirmationCardTypeEnum from '@inter-ikea-kompis/component-summary-page/lib/enums/SummaryPageConfirmationCardTypeEnum';
import SummaryPageTabEnum from '@inter-ikea-kompis/component-summary-page/lib/enums/SummaryPageTabEnum';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import {
	ConfirmationSummaryShareDesignStateEnum,
	ConfigurationSummaryConfirmationCardTypeEnum,
	ConfigurationSummaryConfirmationCardStateEnum,
} from '@inter-ikea-kompis/component-configuration-summary';
import {
	IShoppingProduct,
	IProduct,
	IStore,
	IStoreAvailability,
	IZipAvailability,
	IDexfSettings,
	ITranslations,
	IFailedShoppingItem,
} from '@inter-ikea-kompis/types';
import { ZipInCardStateEnum } from '@inter-ikea-kompis/component-zip-in';

type Props = {
	additionalCovers: IShoppingProduct[];
	hide?: boolean;
	screenshot: string;
	sofaConfiguration: SofaConfiguration;
	shoppingItems: ShoppingItem[];
	settings: Settings;
	platform: Platform;
	deviceType: DeviceType;
	orientation: Orientation;
	writeDirection: WriteDirection;
	ymalProducts?: Array<IProduct>;
	sofaRangeName: SofaRangeName;
	zipState?: ZipInCardStateEnum;
	kompisSettings?: IDexfSettings | null;
	kompisTranslations?: ITranslations | null;
	zipInputValue?: string | null;
	shoppingProducts: IShoppingProduct[];
	includedProducts?: IProduct[];
	stores?: IStore[];
	storeAvailabilities?: IStoreAvailability[] | null;
	zipAvailabilities?: IZipAvailability[] | null;
	selectedTab: SummaryPageTabEnum;
	visibleModal: ISummaryPageVisibleModal;
	selectedIndex?: number;
	showSaveForLater: boolean;
	noValidLink: boolean;
	solutionContainsStorage: boolean;
	solutionContainsSleep: boolean;
	moveYmal: boolean;
	saveVpcState: ConfirmationSummaryShareDesignStateEnum;
	noSummaryPageBackButton: boolean | null;
	addToCartState: AddToBagStateEnum;
	addToListState: AddToBagStateEnum;
	confirmationCardType: ConfigurationSummaryConfirmationCardTypeEnum | null;
	confirmationCardState: ConfigurationSummaryConfirmationCardStateEnum | null;
	showAdditionalCoverCard?: boolean;
	showBanner: boolean;
	bannerHasBeenShown: boolean;
	additionalCoverItemIds: string[];
	shoppingItemIds: string[];
	selectedCover: CoverItem;
	storeId: string;
	inlineMessages: ISummaryPageInlineMessage[];
	vpcCode: string;
	zipCode: string;
	qrCodeUrl: string;
	showTotalPackageWeight: boolean;
	failedShoppingItems?: IFailedShoppingItem[] | null;
	addUnavailableProductsToList?: boolean;
	onAddUnavailableProductsToListChange?: (e: CustomEvent) => void;
	onAddUnavailableToCartAndList?: (e: CustomEvent) => void;
	onAddToCart?: (e: CustomEvent) => void;
	onAddToList?: (e: CustomEvent) => void;
	onSaveVpc?: () => void;
	onYmalAddToCart?: (
		shoppingItems: ShoppingItem[],
		types: { ymalType: YmalType; ymalSectionType: YmalSectionType }
	) => void;
	onYmalAddToList?: (
		shoppingItems: ShoppingItem[],
		types: { ymalType: YmalType; ymalSectionType: YmalSectionType }
	) => void;
	onModalOpen?: (e: CustomEvent) => void;
	onModalClose?: () => void;
	onZipInput: (e: CustomEvent) => void;
	onZipSubmit: (e: CustomEvent) => void;
	onConfirmationCardStateChanged: (e: CustomEvent) => void;
	onAdditionalCoverClick: () => void;
	onRemoveAdditionalCoverItem: (e: CustomEvent) => void;
	onStoreChanged: (e: CustomEvent) => void;
	onCopyDesign: () => void;
	onSortListChange: (e: CustomEvent) => void;
	onProductListExpand: (e: CustomEvent) => void;
	onOpenZipIn: (e: CustomEvent) => void;
};

/**
 * React component.
 *
 */
const SummaryPage = forwardRef<HTMLDivElement, Props>((props, ref) => {
	const {
		additionalCovers,
		failedShoppingItems,
		shoppingProducts,
		platform,
		hide,
		ymalProducts,
		showTotalPackageWeight,
		addUnavailableProductsToList,
		onAddUnavailableProductsToListChange,
		onAddUnavailableToCartAndList,
		onYmalAddToCart,
		onYmalAddToList,
		onModalOpen,
		onModalClose,
		onAdditionalCoverClick,
		onStoreChanged,
		onAddToCart,
		onAddToList,
		onSaveVpc,
		onRemoveAdditionalCoverItem,
		onCopyDesign,
		onSortListChange,
		onProductListExpand,
	} = props;

	const getSections = (): IProductListSection[] => {
		return [
			{
				label: localeService.getTranslation('additionalCoverProductListSectionHeader'),
				products: props.additionalCoverItemIds,
			},
			{
				label: localeService
					.getTranslation('additionalCoverProductListSubsectionHeader')
					.replace('[[applicationname]]', props.sofaRangeName),
				products: props.shoppingItemIds,
			},
		];
	};

	const validateIncludedProductsForTotalWeight = (): boolean => {
		let check = false;
		const childItems: string[] = [];
		const productItems: string[] = [];

		props.shoppingProducts.forEach((shoppingProduct: IShoppingProduct): void => {
			if (shoppingProduct.product.content && shoppingProduct.product.content.child) {
				shoppingProduct.product.content.child.forEach((child) => {
					const splitChildId = child.itemId.split('-');

					if (!childItems.some((childItem) => childItem === splitChildId[1])) {
						childItems.push(splitChildId[1]);
					}
				});
			}
		});

		props.includedProducts &&
			props.includedProducts.forEach((product: IProduct) => {
				const productId = product.itemId.split('-');

				if (!productItems.some((item) => item === productId[1])) {
					productItems.push(productId[1]);
				}
			});

		if (JSON.stringify(childItems.sort()) === JSON.stringify(productItems.sort())) {
			check = true;
		}

		return check;
	};

	const showYmalContainer = props.moveYmal
		? (props.solutionContainsSleep || props.solutionContainsStorage) &&
		  props.showAdditionalCoverCard &&
		  props.selectedTab !== SummaryPageTabEnum.productList
		: false;

	if (validateIncludedProductsForTotalWeight()) {
		return (
			<StyledSummaryPage
				ref={ref}
				hide={hide as boolean}
				platform={props.platform}
				deviceType={props.deviceType}
				orientation={props.orientation}
				noSummaryPageBackButton={props.noSummaryPageBackButton as boolean}
				showBanner={props.showBanner}
				bannerHasBeenShown={props.bannerHasBeenShown}
			>
				{props.noValidLink ? null : (
					<>
						{props.noSummaryPageBackButton ? <CheckoutHeader></CheckoutHeader> : null}
						<SummaryPageSection noBack={props.noSummaryPageBackButton}>
							<HeaderContainer onSummaryPage={true} />
							{props.kompisSettings && props.kompisTranslations && (
								<KompisSummaryPage
									productName={props.sofaRangeName}
									shoppingProducts={[...shoppingProducts, ...additionalCovers]}
									showAddToBag={props.platform !== 'kiosk'}
									showShareDesign={true}
									failedShoppingItems={failedShoppingItems}
									addUnavailableProductsToList={addUnavailableProductsToList}
									onAddUnavailableProductsToListChange={onAddUnavailableProductsToListChange}
									onAddUnavailableToCartAndList={onAddUnavailableToCartAndList}
									addToCartState={props.addToCartState}
									addToListState={props.addToListState}
									settings={props.kompisSettings}
									translations={props.kompisTranslations.kompis}
									locale={localeService.getLocale()}
									visibleModal={props.visibleModal}
									confirmationCardType={
										props.confirmationCardType as SummaryPageConfirmationCardTypeEnum | null
									}
									theme={SkapaTheme}
									screenshotUrl={props.screenshot}
									includedProducts={props.includedProducts as IProduct[]}
									stores={props.stores as IStore[]}
									storeAvailabilities={props.storeAvailabilities}
									storeId={props.storeId}
									vpcCode={props.vpcCode}
									showDesignText={true}
									enableMargins={true}
									showTotalPackageWeight={showTotalPackageWeight}
									homeDelivery={
										props.platform === 'web' ? { zipAvailabilities: props.zipAvailabilities } : null
									}
									screenshotAlt={localeService
										.getTranslation('yourConfigurationText')
										.replace('[[selectedCover]]', props.selectedCover.name as string)}
									messageSection={props.inlineMessages}
									productListSections={
										props.additionalCovers && props.additionalCovers.length > 0
											? getSections()
											: undefined
									}
									productListRemovable={props.additionalCoverItemIds}
									designLink={props.platform === 'kiosk' ? props.qrCodeUrl : null}
									startHeadingsAtH2={props.platform === 'web'}
									disableLinks={true}
									showAssistanceCard={props.platform === 'kiosk'}
									onProductListRemove={(e: CustomEvent): void => {
										onRemoveAdditionalCoverItem && onRemoveAdditionalCoverItem(e);
									}}
									zipCode={props.zipCode}
									zipInCardState={props.zipState}
									onModalOpen={(e: CustomEvent): void => onModalOpen && onModalOpen(e)}
									onModalClose={(): void => onModalClose && onModalClose()}
									onStoreSelectorChange={(e): void => onStoreChanged && onStoreChanged(e)}
									onAddToCart={(e: CustomEvent): void => onAddToCart && onAddToCart(e)}
									onAddToList={(e: CustomEvent): void => onAddToList && onAddToList(e)}
									onShareDesign={(): void => onSaveVpc && onSaveVpc()}
									onCopyDesign={(): void => onCopyDesign && onCopyDesign()}
									onSortListChange={(e): void => onSortListChange && onSortListChange(e)}
									onProductListExpand={(e): void => onProductListExpand && onProductListExpand(e)}
									onOpenZipIn={(e: CustomEvent): void => props.onOpenZipIn(e)}
									onSaveZipCode={(e: CustomEvent): void => props.onZipSubmit(e)}
									onForgetZipCode={(e) => props.onZipSubmit(e)}
								/>
							)}
						</SummaryPageSection>
						{(!props.moveYmal ||
							showYmalContainer ||
							props.selectedTab === SummaryPageTabEnum.productList) &&
							platform === 'web' &&
							((ymalProducts && ymalProducts.length > 0) || props.showAdditionalCoverCard) && (
								<YmalViewContainer
									ymalProducts={props.ymalProducts ? props.ymalProducts : []}
									selectedIndex={props.selectedIndex}
									selectedTab={props.selectedTab}
									showYmalContainer={showYmalContainer || false}
									showAdditionalCoverCard={props.showAdditionalCoverCard}
									onYmalAddToCart={(
										e: CustomEvent,
										types: { ymalType: YmalType; ymalSectionType: YmalSectionType }
									): void => onYmalAddToCart?.(e.detail.shoppingItems, types)}
									onYmalAddToList={(
										e: CustomEvent,
										types: { ymalType: YmalType; ymalSectionType: YmalSectionType }
									): void => onYmalAddToList?.(e.detail.shoppingItems, types)}
									onModalOpen={(e: CustomEvent): void => onModalOpen?.(e)}
									onModalClose={(): void => onModalClose?.()}
									onAdditionalCoverClick={onAdditionalCoverClick}
								/>
							)}

						{props.showSaveForLater && (
							<SaveViewContainer
								saveVpcState={props.saveVpcState}
								confirmationCardType={props.confirmationCardType}
								qrCodeUrl={props.qrCodeUrl}
							/>
						)}
					</>
				)}
			</StyledSummaryPage>
		);
	}
	return null;
});

export default SummaryPage;
